import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';

import { useCurrentAccountId } from '@zapier/design-system-revenue/src/hooks';
import { accountHasReachedPercentageOfTaskLimit } from '@zapier/design-system-revenue/src/utils';

import { taskUpgradeNudges as TaskUpgradeNudgesQuery } from './types/taskUpgradeNudges';

export const taskUpgradeNudgesQuery = gql`
  query taskUpgradeNudges($accountId: Int!) {
    account(id: $accountId) {
      currentUserRole
      id
      plan: planV2 {
        hasMeteredBilling
        id
      }
      taskBufferOverride
      usage {
        tasks {
          total
          limit
        }
      }
    }
  }
`;

export const useTaskLimitUpgradeNudges = () => {
  const { accountId } = useCurrentAccountId();

  const { data, error, loading } = useQuery<TaskUpgradeNudgesQuery>(
    taskUpgradeNudgesQuery,
    {
      skip: !accountId,
      variables: {
        accountId,
      },
    }
  );

  const account = data?.account;
  const accountHasMeteredBilling = account?.plan?.hasMeteredBilling;
  const currentUserRole = account?.currentUserRole;
  const taskBufferMultiplier = account?.taskBufferOverride ?? 1;
  const taskLimit = account?.usage?.tasks?.limit ?? 0;
  const tasksUsed = account?.usage?.tasks?.total ?? 0;

  const hasReached80PercentTaskLimit = accountHasReachedPercentageOfTaskLimit({
    percentage: 0.8,
    taskBufferMultiplier,
    taskLimit,
    tasksUsed,
  });

  const hasReached100PercentTaskLimit = accountHasReachedPercentageOfTaskLimit({
    percentage: 1,
    taskBufferMultiplier,
    taskLimit,
    tasksUsed,
  });

  return {
    error: error
      ? 'There was an error fetching account information. Please try again.'
      : null,
    isEligibleFor80PercentTaskUpgradeNudge:
      hasReached80PercentTaskLimit &&
      currentUserRole !== 'member' &&
      !accountHasMeteredBilling &&
      !hasReached100PercentTaskLimit, // to avoid showing both nudges
    isEligibleFor100PercentTaskUpgradeNudge:
      hasReached100PercentTaskLimit &&
      currentUserRole !== 'member' &&
      !accountHasMeteredBilling,
    isLoading: loading,
  };
};
